import { makeStyles } from '@material-ui/styles'
import { pick, prop } from 'ramda'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import { managementTenantsById } from 'account/components/userManagement/tenants/selectors'
import { listTablePrefs, type TablePrefsParams } from 'app/constants'
import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'
import { useAppSelector } from 'app/store'
import type { ArrayElement } from 'core/actions/Action'
import type InferActionParams from 'core/actions/InferActionParams'
import CreateButton from 'core/components/buttons/CreateButton'
import DeleteDialog from 'core/components/DeleteDialog'
import DocumentMeta from 'core/components/DocumentMeta'
import ListContainer from 'core/containers/ListContainer'
import PopoverButton, { type PopoverButtonItem } from 'core/elements/button/PopoverButton'
import type { GridViewColumn } from 'core/elements/grid/Grid'
import type { GridBatchActionSpec } from 'core/elements/grid/hooks/useGridSelectableRows'
import { HeaderPrimaryActionPortal } from 'core/elements/header/portals'
import Text from 'core/elements/Text'
import ToggleSwitch from 'core/elements/ToggleSwitch'
import useGlobalParams from 'core/hooks/useGlobalParams'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import { type SessionState, sessionActions, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import type Theme from 'core/themes/model'
import { isReadonlyUser } from 'core/utils/helpers'
import { canManageExternalNetworks, getUserRole, isRestrictedSsu } from 'core/utils/rbac'
import { routeManager } from 'core/utils/routes'
import DataKeys from 'k8s/DataKeys'
import { hasPermissions } from 'openstack/helpers'
import { formatTimestampToLocal } from '../vms/helpers'
import { deleteNetwork, listNetworks } from './actions'
import CreateNetworkModal from './CreateNetworkModal'
import CreatePhysicalNetworkModal from './CreatePhysicalNetworkModal'
import EditNetworkModal from './EditNetworkModal'
import EditPhysicalNetworkModal from './EditPhysicalNetworkModal'
import { INetworkDetailsPageTabs } from './network-details/model'
import ShareNetworkWithSpecificTenants from './ShareNetworkWithSpecificTenants'
import { networksSelector } from './selectors'

type ModelDataKey = DataKeys.OpenstackNetworks
type SelectorModel = ArrayElement<ReturnType<typeof networksSelector>>
type ActionParams = InferActionParams<typeof listNetworks>
// @fixme using a type here because of https://github.com/microsoft/TypeScript/issues/15300
type Params = ActionParams & {}

const defaultParams: Params = {
  orderBy: 'created_at',
  orderDirection: 'desc',
}

const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>(
  'OpenstackNetworks',
  listTablePrefs,
)

const searchTargets = [
  'name',
  'id',
  'subnetDetails',
  'provider:network_type',
  'status',
  'availableIps',
  'provider:physical_network',
  'provider:segmentation_id',
]

const searchFunctions = [
  {
    property: 'subnetDetails',
    searchFn: (value, searchString) => {
      return value.some(
        (subnet) =>
          subnet.name.toLocaleLowerCase().includes(searchString.toLocaleLowerCase()) ||
          subnet.cidr.toLocaleLowerCase().includes(searchString.toLocaleLowerCase()),
      )
    },
  },
]

const useNetworkModeBadgeStyles = makeStyles<Theme>((theme) => ({
  badge: {
    display: 'inline-block',
    borderRadius: '4px',
    border: `1px solid ${theme.palette.blue['700']}`,
    background: theme.palette.blue['100'],
    color: theme.palette.blue['700'],
    padding: '0 8px',
    fontWeight: 600,
    fontSize: '12px',
    lineHeight: '20px',
    marginLeft: '8px',
    whiteSpace: 'nowrap',
  },
  nameWithBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  nameLink: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: '0 1 auto',
    minWidth: 0,
  },
}))

const NetworkModeBadge = ({ tags }: { tags?: string[] }) => {
  const classes = useNetworkModeBadgeStyles()
  const isSimpleNetwork = tags?.includes('simple_network')
  return (
    <Text variant="caption2" className={classes.badge}>
      {isSimpleNetwork ? 'Layer 2 Network' : 'Managed Network'}
    </Text>
  )
}

const NameWithBadgeCell = ({ value, item }) => {
  const classes = useNetworkModeBadgeStyles()
  const { history } = useReactRouter()

  const handleClick = () => {
    history.push(
      routeManager.routes.openstack.networkDetails.path({
        id: item.id,
        tab: INetworkDetailsPageTabs.Overview,
      }),
    )
  }

  return (
    <div className={classes.nameWithBadge}>
      <Text
        variant="body2"
        className={classes.nameLink}
        onClick={handleClick}
        style={{ cursor: 'pointer', color: '#0089C7' }}
      >
        {value}
      </Text>
      <NetworkModeBadge tags={item.tags} />
    </div>
  )
}

export const SubnetsCellComponent = ({ value }) => {
  return (
    <>
      {value?.map((subnet) => (
        <React.Fragment key={subnet.id}>
          {/* Hide CIDR for restricted SSU users */}
          {isRestrictedSsu() ? (
            <Text variant="body2">{subnet.name}</Text>
          ) : (
            <Text variant="body2" key={subnet.id}>
              {subnet.name}: <b>{subnet.cidr}</b>
            </Text>
          )}
        </React.Fragment>
      ))}
    </>
  )
}

// Resolves a network's owning project_id to a readable tenant name, falling back
// to the raw id when the tenant isn't in the cache (NLP-FR-004).
export const TenantCell = ({ value }: { value: string }) => {
  const getTenantById = useAppSelector(managementTenantsById)
  return <>{getTenantById(value)?.name ?? value}</>
}

const tenantColumn: GridViewColumn<SelectorModel> = {
  key: 'project_id',
  label: 'Tenant',
  render: (value) => <TenantCell value={value} />,
}

export const SharedWithTenants = ({ value, item }) => {
  return (
    <>
      {value
        ? 'All Tenants'
        : item?.sharedTenants?.length > 0
          ? item?.sharedTenants?.map((i) => i.tenantName).join(', ')
          : 'None'}
    </>
  )
}

const networkColumns: GridViewColumn<SelectorModel>[] = [
  {
    key: 'name',
    label: 'Name',
    width: 'medium',
    render: (value, item) => <NameWithBadgeCell value={value} item={item} />,
  },
  {
    key: 'id',
    label: 'UUID',
    display: false,
  },
  {
    key: 'provider:network_type',
    label: 'Network Type',
    render: (value) => <Text variant="caption1">{String(value).toUpperCase()}</Text>,
  },
  {
    key: 'subnetDetails',
    label: 'Subnets',
    render: (value) => <SubnetsCellComponent value={value} />,
  },
  {
    key: 'port_security_enabled',
    label: 'Port Security',
    render: (val) => (val ? 'Enabled' : 'Disabled'),
  },

  {
    key: 'mtu',
    label: 'MTU',
  },

  {
    key: 'router:external',
    label: 'External',
    render: (val) => (val ? 'Yes' : 'No'),
  },
  {
    key: 'provider:segmentation_id',
    label: 'Segmentation ID',
    render: (val) => (val ? val : 'N/A'),
  },
  {
    key: 'admin_state_up',
    label: 'Admin State',
    render: (val) => (val ? 'Up' : 'Down'),
  },
  {
    key: 'dns_domain',
    label: 'DNS Zone',
    render: (val) => (val ? val : '-'),
  },
  {
    key: 'status',
    label: 'Status',
  },
  {
    key: 'availableIps',
    label: 'Available IPs',
  },
  {
    key: 'isSharedAcrossAllTenants',
    label: 'Shared Tenants',
    render: (value, item) => <SharedWithTenants item={item} value={value} />,
  },
  {
    key: 'created_at',
    label: 'Created',
    render: (time: string) => formatTimestampToLocal(time),
    display: false,
  },
]

const ssuNetworkColumns: GridViewColumn<SelectorModel>[] = [
  {
    key: 'name',
    label: 'Name',
    width: 'medium',
    render: (value, item) => <NameWithBadgeCell value={value} item={item} />,
  },
  {
    key: 'id',
    label: 'UUID',
    display: false,
  },
  {
    key: 'subnetDetails',
    label: 'Subnets',
    render: (value) => <SubnetsCellComponent value={value} />,
  },
  {
    key: 'port_security_enabled',
    label: 'Port Security',
    render: (val) => (val ? 'Enabled' : 'Disabled'),
  },
  {
    key: 'router:external',
    label: 'External',
    render: (val) => (val ? 'Yes' : 'No'),
  },
  {
    key: 'admin_state_up',
    label: 'Admin State',
    render: (val) => (val ? 'Up' : 'Down'),
  },
  {
    key: 'status',
    label: 'Status',
  },
  {
    key: 'shared',
    label: 'Shared Tenants',
    render: (value, item) => <SharedWithTenants item={item} value={value} />,
  },
  {
    key: 'created_at',
    label: 'Created',
    render: (time: string) => formatTimestampToLocal(time),
    display: false,
  },
]

/**
 * Networks List Page - Responsible for displaying and managing OpenStack networks
 * Handles network creation, sharing, and display logic
 * Provides UI for viewing networks, sharing with specific tenants, and creating new networks
 * For Admin users:
 * - When virtualNetworking is enabled
 * - Can view all networks
 * - Can share networks with specific tenants
 * - Can create both physical and virtual networks in their tenant
 * - When virtualNetworking is disabled,
 * - Can only view physical networks (no virtual networks)
 * - Cannot create virtual networks
 * For Self-service users:
 * - Can only view networks in their tenant and shared networks
 * - Can create virtual networks in their tenant
 * - Cannot share networks with other tenants
 *
 * VMware environments redirect to VMware networks page
 */
export default function NetworksListPage() {
  const [selectedNetwork, setSelectedNetwork] = useState<SelectorModel>(null)
  const [showShareNetworkModal, setShowShareNetworkModal] = useState<boolean>(false)
  const { allParams: params, getParamsUpdater } = useGlobalParams(usePrefParams, defaultParams)
  const { history } = useReactRouter()
  const { prefs } = useScopedPreferences()
  const { currentTenant } = prefs
  const session = useSelector(prop<string, SessionState>(sessionStoreKey))
  const { features, virtualNetworking } = session
  const isAdminUser = isAdmin()
  const isReadonlyUserFlag = isReadonlyUser()
  const isVmware = features?.experimental?.pmov2_du_type === 'vmware'
  if (isVmware) {
    history.push(routeManager.routes.openstack.vmwareNetworks.path())
  }

  // "View Networks in All Tenants" toggle preference, persisted in session so it
  // survives navigation (NLP-FR-005). Only ever turned on by admins (NLP-FR-001).
  const [allTenantsPref, setAllTenantsPref] = useState<boolean>(
    session?.networksAllTenants ?? false,
  )
  const dispatch = useDispatch()

  // Effective all-tenants mode is always gated on admin, so a stale session flag on
  // a non-admin (e.g. a demoted user) can never widen the fetch or skip the
  // current-tenant filter below (NLP-FR-001).
  const allTenants = isAdminUser && allTenantsPref

  // Flip and persist the preference (NLP-FR-005). We derive the next value from
  // state (with allTenantsPref in the deps) rather than trusting ToggleSwitch's
  // onClick argument: ToggleSwitch memoizes its handler on [onClick] only, so a
  // stable onClick would capture a stale `active` and break toggling off. Changing
  // this callback each flip forces ToggleSwitch to pick up the current value.
  const handleAllTenantsToggle = useCallback(() => {
    const next = !allTenantsPref
    setAllTenantsPref(next)
    dispatch(sessionActions.updateSession({ networksAllTenants: next }))
  }, [allTenantsPref, dispatch])

  // Fetch networks (across all tenants when the toggle is on) (NLP-FR-002)
  const { message, loading, reload } = useListAction(listNetworks, {
    params: { ...params, allTenants },
  })
  const networks = useAppSelector(networksSelector)

  // Force a refetch whenever the toggle flips. listNetworks is also called
  // param-less by ~25 other components, which leaves an "all params" marker in the
  // shared networks cache; that makes useListAction treat the new allTenants params
  // as already-fetched and skip the request. reload(true) bypasses the cache check.
  // On the initial mount useListAction already loads, so we normally skip — except
  // when the toggle is on from session: the shared cache may hold project-scoped
  // data from another consumer (emptyObj marker), so the mount fetch would skip and
  // we'd show stale data. Force an all-tenants refetch in that case (NLP-FR-002).
  const isFirstRender = useRef(true)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      if (allTenants) reload(true)
      return
    }
    reload(true)
    // Intentionally only depend on allTenants. reload (loadData) is re-created
    // whenever cachedParams changes, i.e. after every fetch — including it here
    // would re-fire reload(true) on each completed refresh and loop. The closure
    // captures the reload for the render where allTenants changed, which already
    // carries the latest params.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTenants])

  // Hide virtual networks only when the blueprint explicitly disables it.
  // session.virtualNetworking starts out `null` and only becomes a real boolean once
  // AuthContainer's getBlueprints() call resolves (core/containers/AuthContainer.tsx) —
  // a call that can come back empty for non-admin sessions (e.g. legacy SSU, PCD-9034),
  // leaving it `null` for the whole session. Treating `null` as "hide" (via `!virtualNetworking`)
  // then hid every managed/virtual network. It's enabled by default in Cluster Blueprint and
  // cannot be disabled in any environment, so only an explicit `false` should hide anything.
  const hideVirtualNetworks = useMemo(() => {
    return virtualNetworking === false
  }, [virtualNetworking])

  // Filter networks based on hideVirtualNetworks.
  // In all-tenants mode we skip the current-tenant filter so networks from every
  // tenant are shown; the hideVirtualNetworks filter still applies (NLP-FR-003).
  const networksInTenant = useMemo(() => {
    const belongsToCurrentTenant = (network: SelectorModel) =>
      network.project_id === currentTenant || network.shared

    const isPhysicalNetwork = (network: SelectorModel) =>
      network['provider:network_type'] === 'physical'

    return networks.filter((network) => {
      if (!allTenants && !belongsToCurrentTenant(network)) return false
      if (hideVirtualNetworks && !isPhysicalNetwork(network)) return false
      return true
    })
  }, [networks, currentTenant, hideVirtualNetworks, allTenants])

  // Show a Tenant column only in all-tenants mode so admins can tell which tenant
  // owns each network (NLP-FR-004). Toggle is admin-only, so base on networkColumns.
  const columns = useMemo(() => {
    if (!isAdminUser) return ssuNetworkColumns
    if (!allTenants) return networkColumns
    return [networkColumns[0], tenantColumn, ...networkColumns.slice(1)]
  }, [isAdminUser, allTenants])

  const batchActions: GridBatchActionSpec<SelectorModel>[] = [
    {
      label: 'Share Network With Specific Tenants',
      icon: 'edit',
      handleAction: ([network]) => {
        setSelectedNetwork(network)
        setShowShareNetworkModal(true)
      },
      refreshAfterSuccess: true,
      cond: (selectedRows) =>
        selectedRows.length === 1 &&
        selectedRows[0]?.project_id === currentTenant && // Only allow sharing networks owned by current tenant
        !selectedRows[0]?.isSharedAcrossAllTenants, // Only allow sharing networks that are not already shared with all tenants
      disabledTooltip: (selectedRows) =>
        selectedRows.length > 1
          ? 'You can only share network with specific tenants for a single network at a time'
          : selectedRows[0]?.isSharedAcrossAllTenants
            ? 'Network is already shared with all tenants'
            : selectedRows[0]?.project_id !== currentTenant
              ? 'You can only share networks owned by your tenant'
              : '',
      permissionsPath: ['neutron', 'create_rbac_policy.target_project:POST:/rbac-policies'],
    },
  ]

  const DeletePhysicalNetworkDialog = useCallback(
    (props) => (
      <DeleteDialog
        {...props}
        deleteAction={deleteNetwork}
        title="Delete Network(s)"
        resourceLabel="Networks"
      />
    ),
    [],
  )

  const items: PopoverButtonItem[] = useMemo(
    () => [
      {
        title: 'Physical Network',
        onClick: () => {
          history.push(routeManager.routes.openstack.createPhysicalNetwork.path())
        },
      },
      {
        title: 'Virtual Network',
        onClick: () => {
          history.push(routeManager.routes.openstack.createVirtualNetwork.path())
        },
      },
    ],
    [],
  )

  /**
   * Renders the appropriate create network button based on user permissions and virtual networking settings.
   * - Non-admin users or when virtual networks are hidden: Simple create button
   * - Admin users with virtual networks enabled: Popover button with options
   */
  const CreateNetworkButton = useMemo(() => {
    if (hideVirtualNetworks || !canManageExternalNetworks()) {
      const handleClick = () => {
        const route = canManageExternalNetworks()
          ? routeManager.routes.openstack.createPhysicalNetwork
          : routeManager.routes.openstack.createNetwork
        history.push(route.path())
      }

      return (
        <CreateButton data-testid="create-network-button-non-admin" onClick={handleClick}>
          Create Network
        </CreateButton>
      )
    }

    return <PopoverButton buttonLabel="Create Network" items={items} />
  }, [hideVirtualNetworks, history, items])

  /**
   * Renders the appropriate network creation modals based on user permissions and virtual networking settings.
   * - Non-admin users: Only network creation modal
   * - Admin users: Physical network modal + conditional virtual network modal
   */
  const NetworkModals = useMemo(() => {
    if (!canManageExternalNetworks()) {
      return <CreateNetworkModal addRoute={routeManager.routes.openstack.createNetwork} />
    }

    return (
      <>
        <CreatePhysicalNetworkModal
          addRoute={routeManager.routes.openstack.createPhysicalNetwork}
        />
        {!hideVirtualNetworks && (
          <CreateNetworkModal addRoute={routeManager.routes.openstack.createVirtualNetwork} />
        )}
      </>
    )
  }, [hideVirtualNetworks, showShareNetworkModal, selectedNetwork])

  const deleteCondition = (selectedRows) => {
    // do not allow sp admin to delete external network
    if (selectedRows.some((network) => !!network['router:external'])) {
      const role = getUserRole()
      if (['admin', 'platform_admin'].includes(role)) {
        return true
      } else {
        return false
      }
    }
    return true
  }

  const deleteDisabledTooltip = 'You do not have permissions to delete this external network.'

  return (
    <>
      <DocumentMeta title="Networks" />
      {/* Need to use hasPermissions here since ListContainer is not managing it */}
      {!isReadonlyUserFlag && (
        <>
          {/* Toggle (admin-only) sits inline at the left of the header actions,
              mirroring the Volumes "View Volumes in All Tenants" placement. */}
          <HeaderPrimaryActionPortal>
            {isAdminUser && (
              <ToggleSwitch
                active={allTenants}
                onClick={handleAllTenantsToggle}
                label="View Networks in All Tenants"
              />
            )}
            {hasPermissions(['neutron', 'create_network:POST:/networks']) && CreateNetworkButton}
          </HeaderPrimaryActionPortal>
          {NetworkModals}
        </>
      )}
      {showShareNetworkModal && (
        <ShareNetworkWithSpecificTenants
          onClose={() => {
            setSelectedNetwork(null)
            setShowShareNetworkModal(false)
          }}
          network={selectedNetwork}
        />
      )}
      <ListContainer<ModelDataKey, SelectorModel>
        listPermsKeyPath={['neutron', 'get_network:GET:/networks']}
        createPermsKeyPath={['neutron', 'create_network:POST:/networks']}
        editPermsKeyPath={['neutron', 'update_network:PUT:/networks/{id}']}
        deletePermsKeyPath={['neutron', 'delete_network:DELETE:/networks/{id}']}
        dataKey={DataKeys.OpenstackNetworks}
        label="Networks"
        searchTargets={searchTargets}
        searchFunctions={searchFunctions}
        uniqueIdentifier="id"
        loading={loading}
        loadingMessage={message}
        onRefresh={reload}
        data={networksInTenant}
        columns={columns}
        getParamsUpdater={getParamsUpdater}
        deleteAction={deleteNetwork}
        DeleteDialogComponent={DeletePhysicalNetworkDialog}
        deleteCond={deleteCondition}
        deleteDisabledInfo={deleteDisabledTooltip}
        EditDialogComponent={
          canManageExternalNetworks() ? EditPhysicalNetworkModal : EditNetworkModal
        }
        batchActions={batchActions}
        multiSelection
        {...pick(listTablePrefs, params)}
      />
    </>
  )
}
